import { transformedDeploymentServicesSelector } from "Reducers/environment/deployment";
import parseDimensions from "src/organization/pages/project/environment/metrics/utils/parseDimensions";
import {
  explodeMetrics,
  mergeSameTimestampData
} from "src/organization/pages/project/environment/metrics/utils/transformMetrics";

import {
  ResourceUnitMetrics,
  ResourcesServiceMetricsType
} from "../totalUsage/type";
import { DimensionNameTyeps, NormalizedPoint, ResourceType } from "../types";

export const reAttachPersistentDisk = (
  serviceData?: ResourcesServiceMetricsType
) => {
  const { points = [], services = [] } = serviceData || {};

  const attached = services.map(service => {
    if (service.metrics?.["/mnt"]) return service;
    const serviceName = service.name;
    const detachedName = `${serviceName}---internal---storage`;

    const pointMetrics = points.find(([n]) => n === detachedName);
    if (!pointMetrics?.[1]?.["/mnt"]) return service;

    const mnt = pointMetrics[1]?.["/mnt"].map(point => {
      const transformed = Object.entries(point).reduce((p, next) => {
        const retachdName = next[0].replace(detachedName, serviceName);
        p[retachdName] = next[1];
        return p;
      }, {});
      return transformed;
    });
    service.metrics["/mnt"] = mnt;
    return service;
  });
  return {
    ...serviceData,
    services: attached
  };
};

const groupByTimestamp = metrics => {
  const list = {};

  for (let i = 0; i < metrics.length; i++) {
    const point = metrics[i];
    const group = list[point.timestamp] || [];
    group.push(point);
    list[point.timestamp] = group;
  }

  return Object.values(list);
};

const filter = <A = any>(array: A[], key: string, expectedValue: string) => {
  return array.filter(item => item[key] === expectedValue);
};

const extractValues = (
  points: NormalizedPoint[],
  dimension: DimensionNameTyeps
) =>
  Array.from<string>(
    new Set(points.map(point => point[dimension]).filter(item => !!item))
  );

const getServiceMetrics = (metrics: NormalizedPoint[], serviceName: string) => {
  const serviceMetrics = filter(metrics, "service", serviceName);
  const mountpoints = extractValues(serviceMetrics, "mountpoint");

  const mergeInstanceTimestamp = data => mergeSameTimestampData(data, true);

  const cpu = groupByTimestamp(filter(serviceMetrics, "type", "cpu")).map(
    mergeInstanceTimestamp
  );

  const memory = groupByTimestamp(filter(serviceMetrics, "type", "memory")).map(
    mergeInstanceTimestamp
  );
  const disk = filter(serviceMetrics, "type", "disk");
  const mountpoint = mountpoints.reduce(function reduceMountpoints(
    storage,
    mountpointName
  ) {
    return Object.assign({}, storage, {
      [mountpointName]: groupByTimestamp(
        filter(disk, "mountpoint", mountpointName)
      ).map(mergeInstanceTimestamp)
    });
  }, {} as ResourceUnitMetrics);

  return {
    ...mountpoint,
    cpu,
    memory
  };
};

const getInstances = (resource?: ResourceType[]) => {
  const v = (resource || []).reduce((acc, resource) => {
    const dimension =
      resource.point && resource.point.dimension
        ? parseDimensions(resource.point.dimension)
        : null;
    if (!dimension?.instance) return acc;

    const service = dimension.service;
    if (!acc[service]) {
      acc[service] = [];
    }
    acc[service].push(dimension.instance);

    return acc;
  }, {});

  const result = {};
  for (const name in v) {
    const instance = Array.from(new Set(v[name]));
    result[name] = instance.length > 1 ? [...instance, "average"] : instance;
  }

  return result;
};

const transform = (data: ResourceType[]) => {
  const metricsPoints = explodeMetrics(data);
  const services = extractValues(metricsPoints, "service").map(serviceName => [
    serviceName,
    getServiceMetrics(metricsPoints, serviceName)
  ]);

  return {
    services: Object.fromEntries(services),
    points: services as unknown as Array<[string, ResourceType]>
  };
};

export const parseGridResource = (
  resource?: ResourceType[],
  betterServices?: ReturnType<typeof transformedDeploymentServicesSelector>
) => {
  if (!resource) return {};
  const transformed = transform(resource);
  if (!transformed?.services || !betterServices) {
    return {};
  }

  const services = Object.entries(betterServices)
    .filter(([serviceId]) => transformed.services[serviceId])
    .map(([serviceId, serviceInfo]) => ({
      id: serviceId,
      ...serviceInfo,
      metrics: transformed.services[serviceId]
    }));

  const instances = getInstances(resource);
  const data = reAttachPersistentDisk({
    ...transformed,
    services
  });

  return { data, instances };
};
